import { useEffect, useRef, useState } from 'react'
import localforage from 'localforage'
import toast from 'react-hot-toast'

// @mui
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import Icon from '@mui/material/Icon'
import Tooltip from '@mui/material/Tooltip'

// MD
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Layout
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'

// Services
import { useAppServices } from 'hook/services'

const PRIORITY_COLOR = { high: 'error', medium: 'warning', low: 'default' }
const TURNS_STORAGE_KEY = 'conversationPriorityTurns'

// Small animated "..." while the assistant is thinking — same idea as
// ChatGPT/Claude's typing indicator, built with plain CSS (no extra libs).
function TypingDots() {
  return (
    <MDBox display="flex" gap={0.5} alignItems="center" py={0.5}>
      {[0, 1, 2].map((i) => (
        <MDBox
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: '#9CA3AF',
            animation: 'bounce 1.2s infinite',
            animationDelay: `${i * 0.15}s`,
            '@keyframes bounce': {
              '0%, 80%, 100%': { opacity: 0.3, transform: 'translateY(0)' },
              '40%': { opacity: 1, transform: 'translateY(-3px)' },
            },
          }}
        />
      ))}
    </MDBox>
  )
}

// One flagged conversation, rendered as a compact card rather than a table
// row — reads more like a chat answer than a spreadsheet.
function PriorityCard({ row, onViewAs }) {
  return (
    <MDBox
      sx={{
        border: '1px solid #EEF0F4',
        borderRadius: '10px',
        p: 1.5,
        mb: 1,
        '&:last-of-type': { mb: 0 },
      }}
    >
      <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Chip
            label={row.priority}
            size="small"
            color={PRIORITY_COLOR[row.priority] || 'default'}
            sx={{ textTransform: 'uppercase', fontSize: 10, height: 20 }}
          />
          <MDTypography variant="button" fontWeight="bold">
            {row.host}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            ↔
          </MDTypography>
          <MDTypography variant="button" fontWeight="bold">
            {row.creative}
          </MDTypography>
        </MDBox>
        <MDTypography variant="caption" color="text" whiteSpace="nowrap">
          {row.lastActivity ? new Date(row.lastActivity).toLocaleString() : ''}
        </MDTypography>
      </MDBox>
      {row.listing && (
        <MDTypography variant="caption" color="text" display="block" mb={0.25}>
          {row.listing}
        </MDTypography>
      )}
      <MDTypography variant="button" sx={{ color: '#5E7C7B' }} display="block" mb={1}>
        {row.reason}
      </MDTypography>

      <MDBox display="flex" gap={1}>
        {row.hostId && (
          <Tooltip title="Log in as the host and open this chat">
            <MDBox
              component="button"
              onClick={() => onViewAs(row.hostId, 'host', row.conversationId)}
              sx={{
                border: '1px solid #BFD9F8',
                background: '#F3F8FF',
                color: '#1662C4',
                borderRadius: '999px',
                px: 1.5,
                py: 0.5,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { background: '#E8F0FE' },
              }}
            >
              View as {row.host}
            </MDBox>
          </Tooltip>
        )}
        {row.creativeId && (
          <Tooltip title="Log in as the creative and open this chat">
            <MDBox
              component="button"
              onClick={() => onViewAs(row.creativeId, 'creative', row.conversationId)}
              sx={{
                border: '1px solid #BFD9F8',
                background: '#F3F8FF',
                color: '#1662C4',
                borderRadius: '999px',
                px: 1.5,
                py: 0.5,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { background: '#E8F0FE' },
              }}
            >
              View as {row.creative}
            </MDBox>
          </Tooltip>
        )}
      </MDBox>
    </MDBox>
  )
}

// A single turn in the conversation: the admin's question (right) and the
// assistant's answer (left) — the core ChatGPT/Claude layout pattern.
function Turn({ turn, onViewAs }) {
  return (
    <MDBox mb={3}>
      {/* Admin's question */}
      <MDBox display="flex" justifyContent="flex-end" mb={1.5}>
        <MDBox
          sx={{
            maxWidth: '75%',
            backgroundColor: '#1A73E8',
            color: '#fff',
            borderRadius: '16px 16px 4px 16px',
            px: 2,
            py: 1.25,
          }}
        >
          <MDTypography variant="button" sx={{ color: '#fff' }}>
            {turn.prompt || 'Check what needs attention'}
          </MDTypography>
        </MDBox>
      </MDBox>

      {/* Assistant's answer */}
      <MDBox display="flex" alignItems="flex-start" gap={1.25}>
        <MDBox
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            backgroundColor: '#E8F0FE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          <Icon sx={{ fontSize: 16, color: '#1662C4' }}>auto_awesome</Icon>
        </MDBox>

        <MDBox
          sx={{
            maxWidth: '85%',
            backgroundColor: '#fff',
            border: '1px solid #EEF0F4',
            borderRadius: '4px 16px 16px 16px',
            px: 2,
            py: 1.5,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            flex: 1,
          }}
        >
          {turn.loading ? (
            <TypingDots />
          ) : turn.error ? (
            <MDTypography variant="button" color="error">
              {turn.error}
            </MDTypography>
          ) : (
            <>
              <MDTypography variant="button" fontWeight="medium" display="block" mb={turn.rows?.length ? 1.25 : 0}>
                {turn.summary}
              </MDTypography>
              {turn.rows?.map((row) => (
                <PriorityCard key={row.conversationId} row={row} onViewAs={onViewAs} />
              ))}
            </>
          )}
        </MDBox>
      </MDBox>
    </MDBox>
  )
}

function ConversationPriority() {
  const AppService = useAppServices()

  const [input, setInput] = useState('')
  const [turns, setTurns] = useState([])
  const [sending, setSending] = useState(false)
  const scrollRef = useRef(null)
  const loadedRef = useRef(false)

  useEffect(() => {
    localforage.getItem(TURNS_STORAGE_KEY).then((saved) => {
      if (saved?.length) setTurns(saved)
      loadedRef.current = true
    })
  }, [])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
    if (loadedRef.current) {
      localforage.setItem(
        TURNS_STORAGE_KEY,
        turns.filter((t) => !t.loading)
      )
    }
  }, [turns])

  // Logs in as the host or creative on that thread and drops them straight
  // into the real chat — same "login as user" the Users page already uses,
  // just pointed at a specific conversation via conversation_id.
  const handleViewAs = async (userId, role, conversationId) => {
    const res = await AppService.auth.loginuser({ payload: { userId, role: 'Admin' } })
    if (!res?.response?.success) {
      toast.error(res?.response?.message || 'Could not log in as that user.')
      return
    }
    const path = role === 'host' ? '/host/messages' : '/creative/messages'
    const redirect = encodeURIComponent(`${path}?conversation_id=${conversationId}`)
    window.location.href = `https://aloca.com.au/?token=${encodeURIComponent(
      res.response.token
    )}&redirect=${redirect}`
  }

  const send = async () => {
    if (sending) return
    const prompt = input.trim()
    const turnId = Date.now()
    setInput('')
    setSending(true)
    setTurns((prev) => [...prev, { id: turnId, prompt, loading: true }])

    try {
      const { response } = await AppService.conversationPriority.check({ payload: { prompt } })
      setTurns((prev) =>
        prev.map((t) =>
          t.id === turnId
            ? response?.success
              ? { ...t, loading: false, summary: response.message, rows: response.data || [] }
              : { ...t, loading: false, error: response?.message || 'Could not check priorities.' }
            : t
        )
      )
    } catch (e) {
      setTurns((prev) =>
        prev.map((t) => (t.id === turnId ? { ...t, loading: false, error: 'Something went wrong.' } : t))
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', minHeight: 480 }}>
              <MDBox
                mx={2}
                mt={-3}
                py={2.5}
                px={2.5}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                alignItems="center"
                gap={1.5}
              >
                <MDBox
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon sx={{ color: 'white' }}>auto_awesome</Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant="h6" color="white" sx={{ lineHeight: 1.2 }}>
                    Conversation Priorities
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                    Last 3 days · on demand, nothing runs in the background
                  </MDTypography>
                </MDBox>
              </MDBox>

              {/* Message history */}
              <MDBox ref={scrollRef} flex={1} sx={{ overflowY: 'auto' }} px={3} pt={3} pb={1}>
                {turns.length === 0 ? (
                  <MDBox
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    gap={1}
                  >
                    <MDBox
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: '#E8F0FE',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1,
                      }}
                    >
                      <Icon sx={{ color: '#1662C4' }}>auto_awesome</Icon>
                    </MDBox>
                    <MDTypography variant="h6">Ask about the last 3 days' conversations</MDTypography>
                    <MDTypography variant="button" color="text" sx={{ maxWidth: 380 }}>
                      Leave it blank and hit send for the default check, or ask something specific —
                      &ldquo;just complaints&rdquo;, &ldquo;anything about cancellations&rdquo;.
                    </MDTypography>
                  </MDBox>
                ) : (
                  turns.map((turn) => <Turn key={turn.id} turn={turn} onViewAs={handleViewAs} />)
                )}
              </MDBox>

              {/* Input bar */}
              <MDBox px={3} py={2} sx={{ borderTop: '1px solid #EEF0F4' }}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    backgroundColor: '#F7F8FA',
                    borderRadius: '999px',
                    pl: 2.5,
                    pr: 0.75,
                    py: 0.5,
                  }}
                >
                  <TextField
                    fullWidth
                    variant="standard"
                    placeholder="Ask about recent conversations, or leave blank…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && send()}
                    InputProps={{ disableUnderline: true }}
                  />
                  <IconButton
                    onClick={send}
                    disabled={sending}
                    sx={{
                      backgroundColor: '#1A73E8',
                      color: '#fff',
                      '&:hover': { backgroundColor: '#1662C4' },
                      '&.Mui-disabled': { backgroundColor: '#CBD9F2', color: '#fff' },
                    }}
                  >
                    <Icon>{sending ? 'hourglass_empty' : 'send'}</Icon>
                  </IconButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  )
}

export default ConversationPriority
